import { useEffect, useRef } from "react";
import { getStoredAccessToken, refreshToken, removeAllTokens } from "~/helpers";
import { useAuthStore } from "~/stores/authStore";

const SKEW_MS = 10 * 60_000;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64Url = parts[1];
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    // pad if needed
    while (base64.length % 4 !== 0) base64 += "=";
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getTokenExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const expSeconds = typeof payload.exp === "number" ? payload.exp : null;
  return expSeconds ? expSeconds * 1000 : null;
}

export function useProactiveTokenRefresh() {
  const timerRef = useRef<number | null>(null);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);

  useEffect(() => {
    // impersonation tokens have no refresh token
    if (isImpersonating) return;
    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const schedule = () => {
      let delay = 0;

      const accessToken = getStoredAccessToken();
      if (accessToken) {
        const expMs = getTokenExpiryMs(accessToken);
        if (expMs) {
          const now = Date.now();
          delay = Math.max(0, expMs - SKEW_MS - now);
        }
      }

      timerRef.current = window.setTimeout(async () => {
        try {
          const newAccessToken = await refreshToken();

          if (newAccessToken) {
            schedule();
          } else {
            removeAllTokens();
          }
        } catch {
          removeAllTokens();
        }
      }, delay);
    };

    schedule();

    return () => {
      clear();
    };
  }, [isImpersonating]);
}
